import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { Table, Button, Space, Popconfirm, message, Form, Modal, Input, DatePicker } from "antd";
import IDFormModal from "./IDFormModal";
import PrintCardPreview from "./PrintCardPreview";
import moment from "moment";
import { apiGetBadges, apiSaveBadge, apiDeleteBadge } from "services/dashboardService";

const IDBadgesPage = () => {
    const [data, setData] = useState();
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [form] = Form.useForm();
    const [selectedPrintData, setSelectedPrintData] = useState(null);
    const [printModalVisible, setPrintModalVisible] = useState(false);

    const [printHeader, setPrintHeader] = useState("");
    const [subHeader, setSubHeader] = useState("");
    const [issueDate, setIssueDate] = useState(moment().format("MM/DD/YYYY"));
    const [expDate, setExpDate] = useState(moment().add(4, "years").format("MM/DD/YYYY"));

    const userDataStore = useSelector((x) => x.userDataStore);
    const appDataStore = useSelector((x) => x.appDataStore);

    console.log('appDataStore', appDataStore);

    useEffect(() => {
        if (appDataStore?.curRegistration?.ID && userDataStore?.token) {
            fetchBadges();
        }
    }, [appDataStore?.curRegistration?.ID]);

    const fetchBadges = () => {
        const params = new URLSearchParams();
        params.append("token", userDataStore.token);
        
        // Add null check for registration_info
        if (appDataStore?.curRegistration?.ID) {
            params.append("club_id", appDataStore?.curRegistration?.ID);
        } else {
            message.error("Club information not found. Please try again later.");
            return;
        }

        apiGetBadges(params)
            .then((res) => {
                const formatted = res.map((item) => ({
                    ...item,
                    key: item.id, // Required for AntD Table
                }));
                setData(formatted);
            })
            .catch((err) => {
                message.error(err.message || "Failed to fetch badges");
            });
    };

    const handleDelete = async (id) => {
        try {
            const params = new URLSearchParams();
            params.append("token", userDataStore.token);
            params.append("id", id);

            await apiDeleteBadge(params);

            message.success("Badge deleted successfully");
            fetchBadges();
        } catch (error) {
            console.error("Delete failed:", error);
            message.error("Failed to delete badge");
        }
    };

    const handleEdit = (record) => {
        setEditingRecord({
            ...record,
            id: record.id, // explicitly keep id
            // Format dob from YYYY-MM-DD (from API) to MM/DD/YYYY for the input field
            dob: record.dob ? moment(record.dob, "YYYY-MM-DD").format("MM/DD/YYYY") : null,
            issueDate: record.issueDate ? moment(record.issueDate, "YYYY-MM-DD") : null,
            expiresDate: record.expiresDate ? moment(record.expiresDate, "YYYY-MM-DD") : null,
        });

        setModalVisible(true);
    };

    const handleNew = () => {
        setEditingRecord(null);
        setModalVisible(true);
    };

    const handleFormFinish = (values) => {
        console.log("🚨 Submitted form values:", values);
        const params = new URLSearchParams();
        params.append("token", userDataStore.token);
        
        // Add null check for registration_info
        if (appDataStore?.curRegistration?.ID) {
            params.append("club_id", appDataStore?.curRegistration?.ID);
        } else {
            message.error("Club information not found. Please try again later.");
            return;
        }

        const isEditing = editingRecord && typeof editingRecord.id !== "undefined";

        if (isEditing) {
            params.append("is_edit", true);
            params.append("id", editingRecord.id);
        }

        Object.entries(values).forEach(([key, val]) => {
            if (key === 'dob' && typeof val === 'string' && val) {
                // Convert dob from MM/DD/YYYY string to YYYY-MM-DD for the API
                params.append(key, moment(val, "MM/DD/YYYY").format("YYYY-MM-DD"));
            } else if (val?._isAMomentObject) {
                params.append(key, val.format("YYYY-MM-DD"));
            } else if (key === 'image' && Array.isArray(val) && val.length > 0) {
                params.append(key, val[0].name);
            } else if (val === undefined || val === null || val === "undefined") {
                params.append(key, "");
            } else {
                params.append(key, val);
            }
        });

        apiSaveBadge(params)
            .then(() => {
                message.success(isEditing ? "ID updated successfully" : "New ID created successfully");
                setModalVisible(false);
                fetchBadges();
            })
            .catch((err) => {
                message.error(err.message || "Failed to save badge");
            });
    };

    const fetchPrintHeader = async () => {
        try {
            const configRes = await fetch('/headers/club_config.txt');
            if (!configRes.ok) throw new Error("Config file not found");
            
            const configText = await configRes.text();
            const lines = configText.split('\n');
            
            let defaultHeader = "";
            let defaultSubHeader = "";
            
            for (const line of lines) {
                if (line.includes('Default_Header=')) {
                    defaultHeader = line.split('=')[1].trim().replace(/"/g, '');
                } else if (line.includes('Default_SubHeader=')) {
                    defaultSubHeader = line.split('=')[1].trim().replace(/"/g, '');
                }
            }

            // Set both header and subheader from config
            setPrintHeader(defaultHeader);
            // setSubHeader(defaultSubHeader);
            
            return defaultHeader;
        } catch (err) {
            console.error("Failed to fetch print header:", err);
            return ""; // Fallback
        }
    };

    const handlePrint = (record) => {
        console.log('handlePrint called for record:', record);
        const updatedRecord = { ...record };

        // Format DOB from YYYY-MM-DD (from API) to MM/DD/YYYY for display
        if (updatedRecord.dob) {
            updatedRecord.dob = moment(updatedRecord.dob, "YYYY-MM-DD").format("MM/DD/YYYY");
        }
        if (updatedRecord.issueDate?._isAMomentObject) {
            updatedRecord.issueDate = updatedRecord.issueDate.format("MM/DD/YYYY");
        }
        if (updatedRecord.expiresDate?._isAMomentObject) {
            updatedRecord.expiresDate = updatedRecord.expiresDate.format("MM/DD/YYYY");
        }

        setSelectedPrintData(updatedRecord);
        // Set subheader default based on classification
        if (updatedRecord.classification === 'Resident') {
            setSubHeader('Resident Card');
            console.log('setSubHeader called with Resident Card in handlePrint');
        } else if (updatedRecord.classification === 'Employee') {
            setSubHeader('Employee ID');
            console.log('setSubHeader called with Employee ID in handlePrint');
        } else {
            setSubHeader('');
            console.log('setSubHeader called with empty string in handlePrint');
        }
        fetchPrintHeader(); // 🛠 fetch default header
        setPrintModalVisible(true);
    };

    const columns = [
        {
            title: "First Name",
            dataIndex: "firstName",
            key: "firstName",
            sorter: (a, b) => a.firstName.localeCompare(b.firstName)
        },
        {
            title: "Last Name",
            dataIndex: "lastName",
            key: "lastName",
            sorter: (a, b) => a.lastName.localeCompare(b.lastName)
        },
        {
            title: "Classification",
            dataIndex: "classification",
            key: "classification",
            filters: [
                { text: "Resident", value: "Resident" },
                { text: "Employee", value: "Employee" },
            ],
            onFilter: (value, record) => record.classification === value,
        },
        {
            title: "Created Date",
            dataIndex: "created_at",
            key: "created_at",
            render: (date) => date ? moment(date).format("MM/DD/YYYY") : ""
        },
        {
            title: "Action",
            key: "action",
            render: (text, record) => (
                <Space>
                    <Button onClick={() => handleEdit(record)}>Edit</Button>
                    <Button onClick={() => handlePrint(record)}>Print</Button> {/* Print button */}
                    <Popconfirm title="Are you sure to delete this ID?" onConfirm={() => handleDelete(record.key)}>
                        <Button danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const handlePrintCard = () => {
        const printContents = document.getElementById("print-area").innerHTML;
        const originalContents = document.body.innerHTML;

        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
        window.location.reload(); // Optional: reloads page after printing
    };

    useEffect(() => {
        fetchPrintHeader();
    }, []);

    console.log('subHeader', subHeader);

    return (
        <div style={{ padding: 32 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                <h1>ID Badges</h1>
                <Space>
                <Button type="primary" onClick={handleNew}>
                    + New ID
                </Button>
                </Space>
            </div>

            <Table columns={columns} dataSource={data} />

            <IDFormModal
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onFinish={handleFormFinish}
                initialValues={editingRecord}
                form={form}
            />

            {/* Print Preview Modal */}
            <Modal
                title="Print Preview"
                visible={printModalVisible}
                onCancel={() => {
                  setPrintModalVisible(false);
                  setSubHeader("");
                  console.log('setSubHeader called with empty string on modal close');
                }}
                footer={null}
                width={1400}
                bodyStyle={{ 
                    padding: '20px',
                    overflowX: 'hidden' // Prevent horizontal scroll on modal body
                }}
            >
                <div style={{ 
                    display: 'flex', 
                    gap: '20px',
                    overflowX: 'hidden' // Prevent horizontal scroll on container
                }}>
                    {/* Controls Section */}
                    <div style={{ flex: '0 0 300px' }}>
                        <Form layout="vertical">
                            <Form.Item label="Header">
                            <Input
                                value={printHeader}
                                onChange={(e) => setPrintHeader(e.target.value)}
                            />
                            </Form.Item>
                            <Form.Item label="Sub Header">
                            <Input
                                value={subHeader}
                                onChange={(e) => setSubHeader(e.target.value)}
                            />
                            {console.log('Sub Header field (left edit part):', subHeader)}
                            </Form.Item>
                            {/* Conditionally render date fields based on classification */}
                            {selectedPrintData?.classification === 'Resident' && (
                              <>
                                <Form.Item label="DOB">
                                  <Input value={selectedPrintData?.dob || ''} disabled />
                                </Form.Item>
                                <Form.Item label="Issue Date (MM/DD/YYYY)">
                                  <Input
                                    value={issueDate}
                                    onChange={(e) => setIssueDate(e.target.value)}
                                    placeholder="MM/DD/YYYY"
                                  />
                                </Form.Item>
                                <Form.Item label="Expiration Date (MM/DD/YYYY)">
                                  <Input
                                    value={expDate}
                                    onChange={(e) => setExpDate(e.target.value)}
                                    placeholder="MM/DD/YYYY"
                                  />
                                </Form.Item>
                              </>
                            )}
                            {selectedPrintData?.classification === 'Employee' && (
                              <Form.Item label="Issue Date (MM/DD/YYYY)">
                                <Input
                                  value={issueDate}
                                  onChange={(e) => setIssueDate(e.target.value)}
                                  placeholder="MM/DD/YYYY"
                                />
                              </Form.Item>
                            )}
                        <Button type="primary" onClick={handlePrintCard} block>
                                Print Card
                        </Button>
                        </Form>
                    </div>

                    {/* Preview Section */}
                    <div style={{ 
                        flex: '1', 
                        overflowY: 'auto', // Only allow vertical scrolling
                        maxHeight: '800px',
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '20px',
                        overflowX: 'hidden' // Prevent horizontal scroll
                    }}>
                        <div id="print-area" style={{ 
                            width: '1006px',
                            overflow: 'hidden' // Prevent any overflow
                        }}>
                            {console.log('PrintCardPreview props: classification:', selectedPrintData?.classification, 'subHeader:', subHeader)}
                            <PrintCardPreview
                                idData={selectedPrintData}
                                printHeader={printHeader}
                                subHeader={subHeader}
                                issueDate={issueDate}
                                expDate={expDate}
                                clubLogoUrl={appDataStore?.curRegistration?.homepage_logo}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default IDBadgesPage;
